import Phaser from 'phaser';
import { hash } from './animalBehavior.js';
import { perspectiveScale } from './carCatalog.mjs';

const ANIMAL_SIZE = 100;
// Per-type display-size multiplier (cows are big farm animals).
const SIZE_SCALE = { krowa: 1.5 };

// Badge art for a pending product stack. A stack of 2..5 has dedicated art
// (eggs_N / milk_N — a little bigger than a single item). A single hen's egg comes in
// two variants, picked per STACK: last_collected_iso changes only when the stack is
// collected, so the look is stable until then and identical on every client
// (visitors too). The shop keeps the separate product_jajko art.
const STACK_BADGE_MAX = 5;
const SINGLE_BADGE_SIZE = 40;
const STACK_BADGE_SIZE = 52;

function productBadgeTexture(animal) {
  const n = Math.min(animal.pending_count ?? 0, STACK_BADGE_MAX);
  if (animal.type !== 'kura') return n >= 2 ? `milk_${n}` : 'product_mleko';
  if (n >= 2) return `eggs_${n}`;
  return `egg_${1 + (hash(animal.id, animal.last_collected_iso ?? '') % 2)}`;
}

export class AnimalSprite extends Phaser.GameObjects.Container {
  constructor(scene, x, y, animal) {
    super(scene, x, y);
    this.animal = animal;
    this.scene = scene;

    const key = scene.textures.exists(`animal_${animal.type}`) ? `animal_${animal.type}` : 'natalka';
    // Outdoor scenes draw people at real-world scale (scene.worldScale); a pet next to a
    // 120 px child has to shrink with her or it stands chest-high.
    const ws = scene.worldScale ?? 1;
    const size = ANIMAL_SIZE * (SIZE_SCALE[animal.type] ?? 1) * ws;
    this.body = scene.add.image(0, 0, key).setDisplaySize(size, size).setInteractive({ useHandCursor: true });
    this.add(this.body);
    this._half = size / 2;

    this.hungryBadge = scene.add.image(40 * ws, -50 * ws, 'icon_hungry').setDisplaySize(32, 32).setVisible(false);
    this.add(this.hungryBadge);

    this.productBadge = scene.add.image(0, -60 * ws, 'product_jajko')
      .setDisplaySize(SINGLE_BADGE_SIZE, SINGLE_BADGE_SIZE).setVisible(false);
    this.add(this.productBadge);

    this._lastX = x;          // for facing
    this._bobPhase = Math.random() * Math.PI * 2;

    scene.add.existing(this);
  }

  // Drive position/facing/visibility from a behavior RenderState. dt in ms.
  applyRenderState(rs, dt) {
    if (rs.hidden) {
      this.setVisible(false);   // animal is fully hidden inside the buda at night
      return;
    }
    this.setVisible(true);

    // Smoothly follow the target. The target itself now walks at a natural pace
    // (waypoint wander), so a light ease just tracks it — and softens the snap when
    // an interaction/flee/follow overrides the target.
    const k = Math.min(1, dt / 1000 * 6);
    const prevX = this.x;
    let mx = (rs.x - this.x) * k;
    let my = (rs.y - this.y) * k;
    // Cap the step to a walking speed so a target jump (a meeting starting/ending,
    // emerging in the morning, …) is walked to rather than teleported across.
    const maxStep = 130 * dt / 1000;
    const len = Math.hypot(mx, my);
    if (len > maxStep) { mx = mx / len * maxStep; my = my / len * maxStep; }
    this.x += mx;
    this.y += my;
    // a room or yard drawn in perspective: the animal is smaller the further up its feet stand
    if (this.scene.perspective) this.setScale(perspectiveScale(this.y + this._half, this.scene.perspective));

    // Facing: flip toward horizontal travel. No bob — the body stays grounded.
    const vx = this.x - prevX;
    if (Math.abs(vx) > 0.3) this.body.setFlipX(vx < 0);
    this.body.y = 0;

    // Badges: hunger and product are independent — a hungry animal can still be
    // sitting on a full product stack, and both should show (they sit at different
    // offsets so they don't overlap). `rs.badge === 'sleep'` only fires when the
    // sprite itself is hidden at night, which the `rs.hidden` branch above already
    // covers, so both badges are simply gated on visibility here.
    this.hungryBadge.setVisible(!rs.hidden && !!this.animal.is_hungry);
    this._syncProductBadge(this.animal, !rs.hidden);
  }

  _syncProductBadge(animal, show) {
    const n = animal.pending_count ?? 0;
    const visible = show && n > 0;
    this.productBadge.setVisible(visible);
    if (visible) {
      const key = productBadgeTexture(animal);
      if (this.scene.textures.exists(key)) this.productBadge.setTexture(key);
      const size = n >= 2 ? STACK_BADGE_SIZE : SINGLE_BADGE_SIZE;
      this.productBadge.setDisplaySize(size, size);
    }
  }

  update(animalState, _catalog) {
    this.animal = animalState;
    this.hungryBadge.setVisible(!!animalState.is_hungry);
    this._syncProductBadge(animalState, true);
  }

  onClick(handler) {
    this.body.on('pointerup', handler);
  }

  showPetHeart() {
    if (!this.scene.textures.exists('icon_pet_heart')) return;
    const h = this.scene.add.image(this.x, this.y - 40, 'icon_pet_heart').setDisplaySize(40, 40);
    this.scene.tweens.add({
      targets: h,
      y: h.y - 60,
      alpha: 0,
      duration: 800,
      onComplete: () => h.destroy(),
    });
  }
}
